<template>
	<div>
		<div v-if="!isMobile">
			<div class="single">
				<v-menu v-model="showDatePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
					<template v-slot:activator="{ props }">
						<v-text-field
							v-if="showField"
							v-model="date"
							@blur="emitBlur(date, 'single')"
							:label="$t(label)"
							:disabled="disabled"
							:error="isInvalid"
							:rules="extendedRules"
							color="primary"
							variant="underlined"
							dense
							ref="dateField"
						></v-text-field>
						<div :class="showField ? 'picker-button-label' : ''">
							<div v-if="!showField" class="label">{{ $t(label) }}</div>
							<v-btn
								v-if="!hideIcon"
								size="medium-large"
								@click="showDatePicker = !showDatePicker"
								v-bind="props"
								:disabled="disabled"
								icon
								:aria-label="$t(label)"
								variant="text"
							>
								<v-icon size="large">mdi-calendar</v-icon>
							</v-btn>
						</div>
					</template>

					<v-date-picker
						v-model="pickedDate"
						color="primary"
						:disabled="disabled"
						:view-mode="pickerType"
						@change="datePickerChanged = true"
						:class="['datetime-picker', { 'hide-nav-arrows': hideDay }]"
						:max="max"
						:min="min"
						@update:modelValue="datePickerChanged = true"
						@update:month="handleMonthChange"
						first-day-of-week="1"
					></v-date-picker>
				</v-menu>
			</div>
		</div>
		<div v-if="isMobile">
			<v-menu v-model="showDatePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
				<template v-slot:activator="{ props }">
					<v-text-field
						v-model="date"
						@blur="emitBlur(date, 'single')"
						:label="$t(label)"
						:disabled="disabled"
						:error="isInvalid"
						:append-icon="hideIcon ? '' : 'mdi-calendar'"
						:rules="extendedRules"
						color="primary"
						variant="underlined"
						dense
						v-bind="props"
						ref="dateFieldMobile"
					></v-text-field>
				</template>

				<v-date-picker
					v-model="pickedDate"
					color="primary"
					:disabled="disabled"
					:view-mode="pickerType"
					:class="['datetime-picker', { 'hide-nav-arrows': hideDay }]"
					:max="max"
					:min="min"
					@update:modelValue="datePickerChanged = true"
					first-day-of-week="1"
				></v-date-picker>
			</v-menu>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import { parseDate } from '../../utils/utils.js';

export default {
	props: {
		modelValue: {
			type: String,
		},
		label: {
			type: String,
			default: 'day_field_date',
		},

		disabled: {
			type: Boolean,
			default: false,
		},

		hideIcon: {
			type: Boolean,
			default: false,
		},

		hideDay: {
			type: Boolean,
			default: false,
		},
		isMobile: {
			type: Boolean,
			default: false,
		},
		min: {
			type: String,
			default: '',
		},
		max: {
			type: String,
			default: '',
		},
		rules: {
			type: Array,
			default: () => [],
		},
		lastOfMonth: {
			type: Boolean,
			default: false,
		},
		showField: { type: Boolean, default: true },
	},
	emits: ['update:modelValue', 'change', 'blur', 'clear'],
	data() {
		return {
			date: '',
			showDatePicker: false,
			pickedDate: undefined,
			inPromise: false,
			promiseCounter: 0,
			isInvalid: false,
			datePickerChanged: false,
			isMinDateInvalid: false,
		};
	},
	computed: {
		...mapState({
			locale: (state) => state.profile.locale,
			localeKey: (state) => state.profile.locale.key,
		}),
		vuetifyLocale() {
			if (!this.localeKey) return 'en';
			return this.localeKey.split('-')[0].split('_')[0];
		},
		extendedRules() {
			const extendedRules = [...this.$props.rules];
			const validateDate = (v) => {
				if (v === '') {
					// Consideration: The default is no error for empty fields
					return true;
				}
				if (this.isMinDateInvalid) {
					return this.$t('minimum_date_invalid');
				}
				if (this.isInvalid) {
					// Date was set to invalid by this.handleInput or this.validateUpdateAndSetFlags
					return this.$t('enter_valid_date');
				}
				try {
					const formats = [this.locale.dateFormat, this.locale.dateFormatParse];
					const formatsWithoutDay = [this.locale.dateFormatWithoutDay, this.locale.dateFormatWithoutDayParse];
					const parsedDate = parseDate(v, this.hideDay, formatsWithoutDay, formats);
					if (parsedDate === null) {
						// Unparsable date object
						return this.$t('enter_valid_date');
					}
					const date = parsedDate.toJSDate();
					if (isNaN(date)) {
						// Invalid js date object
						return this.$t('enter_valid_date');
					}

					return true;
				} catch {
					// Error while parsing
					return this.$t('enter_valid_date');
				}
			};
			extendedRules.unshift(validateDate);
			return extendedRules;
		},
		pickerType() {
			return this.hideDay ? 'months' : 'month';
		},
	},
	methods: {
		handleInput(date, typeOfInput) {
			if (date === '') {
				this.validateUpdateAndSetFlags(date, typeOfInput);
				return;
			}
			if (date === null || date === undefined) {
				return;
			}

			// Try to parse the input -> null or luxon.DateTime
			const formats = [this.locale.dateFormat, this.locale.dateFormatParse];
			const formatsWithoutDay = [this.locale.dateFormatWithoutDay, this.locale.dateFormatWithoutDayParse];
			const parsedDate = parseDate(date, this.hideDay, formatsWithoutDay, formats);

			if (this.isValidDate(parsedDate)) {
				let parsedISODate = parsedDate.toISODate();

				if (this.hideDay && this.lastOfMonth) {
					parsedISODate = parsedDate.endOf('month').toISODate();
				}

				if (this.min && DateTime.fromISO(parsedISODate) < DateTime.fromISO(this.min)) {
					this.isInvalid = true;
					this.isMinDateInvalid = true;
					this.validate();
					return;
				}
				this.validateUpdateAndSetFlags(parsedISODate, typeOfInput);
				this.$emit('change', parsedISODate);

				this.isMinDateInvalid = false;
				this.validateUpdateAndSetFlags(parsedISODate);
			} else {
				this.isInvalid = true;
				this.isMinDateInvalid = false;
				this.validate();
			}
		},

		validateUpdateAndSetFlags(currentDate) {
			const isValid = currentDate ? this.isValidDate(currentDate) : false;
			if (isValid) {
				this.isInvalid = false;
				this.isMinDateInvalid = false;
				this.updateModel(currentDate);
			} else if (currentDate) {
				// null? but not empty case
				this.isInvalid = true;
			} else {
				// empty input
				this.isInvalid = false;
				this.isMinDateInvalid = false;
				this.datePickerChanged = false;
				this.pickedDate = undefined;
				this.$emit('update:modelValue', '');
			}
			// Consideration: Retrigger the validation to update the field status
			this.validate();
		},

		validate() {
			if (this.isMobile) {
				this.$refs.dateFieldMobile.validate();
				return;
			}
			this.$refs.dateField.validate();
		},

		updateModel(currentDate) {
			this.datePickerChanged = true;
			this.pickedDate = new Date(currentDate);
		},
		isValidDate(date) {
			if (!date) {
				return false;
			}

			const parsedDate = DateTime.fromISO(date);

			return parsedDate.isValid;
		},
		emitBlur(date, typeOfInput) {
			this.handleInput(date, typeOfInput);
			this.$emit('blur');
		},
		convertToISODate(date) {
			if (!date || date === undefined) return null;

			let parsedDate;

			// already ISO (yyyy-MM-dd)
			if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
				parsedDate = DateTime.fromISO(date);
			} // ISO datetime (yyyy-MM-ddTHH:mm:ss or similar)
			else if (/^\d{4}-\d{2}-\d{2}T/.test(date)) {
				parsedDate = DateTime.fromISO(date);
			}
			// manual input (MM/dd/yy)
			else if (/^\d{2}\/\d{2}\/\d{2}$/.test(date)) {
				parsedDate = DateTime.fromFormat(date, 'MM/dd/yy');
			}
			// manual input (MM/dd/yyyy)
			else if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
				parsedDate = DateTime.fromFormat(date, 'MM/dd/yyyy');
			} else {
				return null;
			}

			if (!parsedDate.isValid) return null;

			return this.hideDay ? parsedDate.toFormat('yyyy-MM') : parsedDate.toISODate();
		},
		setPickedDate() {
			if (!this.modelValue) {
				this.date = '';
				return;
			}
			const date = this.modelValue ? this.convertToISODate(this.modelValue) : '';
			if (this.isValidDate(date)) {
				this.isInvalid = false;
				this.pickedDate = new Date(date);
			} else {
				this.pickedDate = new Date();
			}
		},
		formatPickedDate(pickedDate) {
			if (!pickedDate) return '';
			if (this.hideDay) {
				return DateTime.fromJSDate(pickedDate).toFormat(this.locale?.dateFormatWithoutDay ?? 'MM.yyyy');
			}
			return DateTime.fromJSDate(pickedDate).toFormat(this.locale?.dateFormat ?? 'dd.MM.yyyy');
		},
		syncVuetifyLocale() {
			if (this.$vuetify?.locale) {
				this.$vuetify.locale.current = this.vuetifyLocale;
			}
		},
		handleMonthChange(v) {
			const yearBtn = document.querySelector('.v-date-picker-controls__year .v-date-picker-controls__only-year-btn .v-btn__content');

			if (this.hideDay && yearBtn) {
				const yearValue = yearBtn.innerText;
				this.pickedDate = new Date(`${yearValue}-${String(v + 1).padStart(2, '0')}-01`);
				this.datePickerChanged = true;
			}
		},
	},

	watch: {
		localeKey: {
			immediate: true,
			handler() {
				this.syncVuetifyLocale();
				if (this.pickedDate) {
					this.date = this.formatPickedDate(this.pickedDate);
				}
			},
		},
		pickedDate(pickedDate) {
			this.date = this.formatPickedDate(pickedDate);
			const isoDate = DateTime.fromJSDate(pickedDate ?? new Date()).toISODate();

			if (pickedDate && this.isValidDate(isoDate)) {
				this.$emit('update:modelValue', isoDate);
			}
			if (!pickedDate) {
				this.$emit('clear');
			}
			if (this.datePickerChanged) {
				this.$emit('change', pickedDate);
				this.datePickerChanged = false;
				this.showDatePicker = false;
			}
		},

		min() {
			if (this.date) {
				// Reset stale min error before re-checking
				this.isMinDateInvalid = false;
				this.isInvalid = false;
				this.$nextTick(() => {
					this.handleInput(this.date);
				});
			}
		},

		modelValue() {
			this.setPickedDate();
		},
	},
	mounted() {
		this.setPickedDate();
	},
};
</script>

<style scoped>
.single {
	display: flex;
	align-items: center;
	justify-content: center;
}

div:has(> div.datetime-picker) {
	box-shadow: none;
}

:deep(.hide-nav-arrows.v-picker .v-date-picker-controls .v-btn:disabled) {
	visibility: hidden;
}

div.datetime-picker {
	box-shadow:
		0 5px 5px -3px rgba(0, 0, 0, 0.2),
		0 8px 10px 1px rgba(0, 0, 0, 0.14),
		0 3px 14px 2px rgba(0, 0, 0, 0.12);
}

.picker-button-label {
	margin-left: 6px;
}

.label {
	opacity: var(--v-medium-emphasis-opacity);
	font-size: 12px;
	letter-spacing: 0.009375em;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
:deep(.v-field--disabled input) {
	color: rgba(0, 0, 0, 0.87);
	opacity: 1;
}
</style>
